/** `sessionStorage` key for the URL a visitor asked for before the login took them away. */
const RETURN_URL_KEY = 'abook:return-url';

/**
 * Whether a URL is safe to navigate back to after sign-in
 * @remarks Only app-relative paths: `//host` would leave the app (open redirect), and the
 * authentication pages are where the login itself runs, so returning there would loop.
 */
function isReturnableUrl(url: string | null | undefined): url is string {
  return !!url && url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/authentication');
}

/**
 * Remembers the URL a visitor without a session asked for, so the login can bring them back
 * @remarks The identity provider always returns to the app root (`redirectUrl`), which would
 * otherwise drop every deep link — e.g. a KMD link to `/associations/redirect/edit?CustomerId=…`.
 * Not angular-auth-oidc-client's own redirect store (`autoLoginPartialRoutesGuard`): the library
 * navigates to it from inside `checkAuth()`, which here runs in an app initializer before the
 * router's first navigation — the timing `AuthenticationService.checkUser()` exists to avoid.
 * Adopting that guard later would mean two redirects racing; drop this note if it is adopted.
 * Each sign-in overwrites the note, so one that was backed out of cannot steer a later login.
 * @param url Router URL that was asked for; none (or one not safe to follow) clears the note
 */
export function rememberReturnUrl(url: string | null | undefined): void {
  try {
    if (isReturnableUrl(url)) {
      sessionStorage.setItem(RETURN_URL_KEY, url);
    } else {
      sessionStorage.removeItem(RETURN_URL_KEY);
    }
  } catch {
    // Storage blocked: the visitor lands on the default page instead.
  }
}

/**
 * The remembered return URL, removed as it is read so it is followed once
 * @returns The URL, or null when there is none (or it is not safe to follow)
 */
export function takeReturnUrl(): string | null {
  try {
    const url = sessionStorage.getItem(RETURN_URL_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    return isReturnableUrl(url) ? url : null;
  } catch {
    return null;
  }
}
